import { config } from './config';

const verifierKey = 'dos.pkce.verifier';
const tokenKey = 'dos.auth.tokens';

export interface AuthTokens {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

function base64Url(bytes: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

async function sha256(value: string): Promise<ArrayBuffer> {
  return crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
}

export function getStoredTokens(): AuthTokens | null {
  const raw = sessionStorage.getItem(tokenKey);
  return raw ? (JSON.parse(raw) as AuthTokens) : null;
}

export function storeTokens(tokens: AuthTokens): void {
  sessionStorage.setItem(tokenKey, JSON.stringify(tokens));
}

export function clearTokens(): void {
  sessionStorage.removeItem(tokenKey);
}

export async function signIn(assignLocation: (url: string) => void = window.location.assign.bind(window.location)): Promise<void> {
  const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)).buffer);
  const challenge = base64Url(await sha256(verifier));
  sessionStorage.setItem(verifierKey, verifier);

  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: `${config.appUrl}/callback`,
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });

  assignLocation(`${config.cognitoDomain}/oauth2/authorize?${params.toString()}`);
}

export async function exchangeCode(code: string): Promise<AuthTokens> {
  const verifier = sessionStorage.getItem(verifierKey);
  if (!verifier) throw new Error('Missing PKCE verifier');

  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.userPoolClientId,
    code,
    redirect_uri: `${config.appUrl}/callback`,
    code_verifier: verifier,
  });

  const res = await fetch(`${config.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!res.ok) {
    throw new Error(`Token exchange failed: ${res.status}`);
  }

  const tokens = (await res.json()) as AuthTokens;
  storeTokens(tokens);
  sessionStorage.removeItem(verifierKey);
  return tokens;
}

export function signOut(): void {
  clearTokens();
  const params = new URLSearchParams({
    client_id: config.userPoolClientId,
    logout_uri: config.appUrl,
  });
  window.location.assign(`${config.cognitoDomain}/logout?${params.toString()}`);
}

export function decodeClaims(idToken: string): Record<string, unknown> {
  const [, payload] = idToken.split('.');
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
  return JSON.parse(atob(padded)) as Record<string, unknown>;
}
